
import React from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormLabel } from '@/components/ui/form';
import { PricingTier } from './types';
import { applyMarkup } from '@/utils/pricing-utils';

interface PricingTiersProps {
  pricingTiers: PricingTier[];
  setPricingTiers: React.Dispatch<React.SetStateAction<PricingTier[]>>;
  baseCost: number;
  markupPercentage?: number;
}

const PricingTiers: React.FC<PricingTiersProps> = ({
  pricingTiers,
  setPricingTiers,
  baseCost,
  markupPercentage = 0
}) => {
  const addPricingTier = () => {
    // Find the highest max quantity from existing tiers
    let startQty = 1;
    if (pricingTiers.length > 0) {
      const highestTier = [...pricingTiers].sort((a, b) => 
        (b.qty_max || Infinity) - (a.qty_max || Infinity)
      )[0];
      
      // If the highest tier has a max value, start from that + 1
      if (highestTier.qty_max) {
        startQty = highestTier.qty_max + 1;
      } else {
        // Otherwise, start from the min value of the last tier
        startQty = highestTier.qty_min;
      }
    }
    
    setPricingTiers([
      ...pricingTiers,
      { 
        qty_min: startQty, 
        qty_max: null, 
        unit_cost: baseCost,
        custom_markup: markupPercentage
      },
    ]);
  };

  const removePricingTier = (index: number) => {
    setPricingTiers(pricingTiers.filter((_, i) => i !== index));
  };

  const updatePricingTier = (index: number, field: keyof PricingTier, value: any) => {
    const updatedTiers = [...pricingTiers];
    updatedTiers[index] = { ...updatedTiers[index], [field]: value };
    
    // Sort tiers by min quantity for better visualization
    const sortedTiers = updatedTiers.sort((a, b) => a.qty_min - b.qty_min);
    
    setPricingTiers(sortedTiers);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold">Bulk Pricing Tiers</h3>
          <p className="text-sm text-muted-foreground">
            Lower bound is inclusive, upper bound is exclusive.
            For example: 100-200 means from 100 up to but not including 200.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={addPricingTier}>
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Tier
        </Button>
      </div>

      {pricingTiers.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3 text-sm font-medium text-muted-foreground">Min Qty</div>
            <div className="col-span-3 text-sm font-medium text-muted-foreground">Max Qty</div>
            <div className="col-span-2 text-sm font-medium text-muted-foreground">Unit Cost</div>
            <div className="col-span-3 text-sm font-medium text-muted-foreground">
              Final Price (with markup)
            </div>
            <div className="col-span-1"></div>
          </div>
          
          {pricingTiers.map((tier, index) => {
            // Calculate final price for this tier with markup applied
            const tierMarkup = tier.custom_markup !== undefined ? tier.custom_markup : markupPercentage;
            const finalPrice = applyMarkup(tier.unit_cost, tierMarkup);
            
            return (
              <div key={index} className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-3">
                  <Input
                    id={`tier-min-${index}`}
                    name={`tier-min-${index}`}
                    type="number"
                    value={tier.qty_min}
                    onChange={(e) => updatePricingTier(index, 'qty_min', Number(e.target.value))}
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    id={`tier-max-${index}`}
                    name={`tier-max-${index}`}
                    type="number"
                    value={tier.qty_max || ''}
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : null;
                      updatePricingTier(index, 'qty_max', value);
                    }}
                    placeholder="No maximum"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    id={`tier-cost-${index}`}
                    name={`tier-cost-${index}`}
                    type="number"
                    step="0.01"
                    value={tier.unit_cost}
                    onChange={(e) => updatePricingTier(index, 'unit_cost', Number(e.target.value))}
                  />
                </div>
                <div className="col-span-3">
                  <div className="h-10 px-3 py-2 bg-slate-50 border rounded-md text-slate-700 flex items-center">
                    R {finalPrice.toFixed(2)}
                  </div>
                </div>
                <div className="col-span-1 flex justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removePricingTier(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PricingTiers;
